import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { login } from '../lib/api';
import { setToken, setUser, isAuthenticated } from '../lib/auth';
import { toast } from 'sonner';
import { LogIn, ArrowRight, Eye, EyeOff, Mail, Lock } from 'lucide-react';
import GoogleSignInButton from '../components/GoogleSignInButton';
import AppleSignInButton from '../components/AppleSignInButton';
import LoginIntro from '../components/LoginIntro';
import { useImmersiveStatusBar } from '../lib/useImmersiveStatusBar';

export default function LoginPage() {
  useImmersiveStatusBar();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showIntro, setShowIntro] = useState(() => !isAuthenticated());
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // Already logged in? Go straight to Home (persistent session).
  useEffect(() => {
    if (isAuthenticated()) {
      navigate('/home', { replace: true });
    }
  }, [navigate]);

  // Show success message if redirected from verification
  useEffect(() => {
    if (searchParams.get('verified') === 'true') {
      toast.success('Email verified! You can now login.');
    }
  }, [searchParams]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await login(email, password);
      setToken(response.data.token);
      setUser(response.data.user);
      toast.success('Welcome back!');
      navigate('/home');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="relative h-[100dvh] w-full flex flex-col md:justify-center md:items-center overflow-hidden"
      data-testid="login-page"
    >
      {showIntro && <LoginIntro onDone={() => setShowIntro(false)} />}
      {/* Mobile background: full illustration width (not zoomed/cropped), pinned to top */}
      <div
        className="fixed inset-0 -z-10 md:hidden"
        style={{
          backgroundImage: "url('/login-bg-mobile.png')",
          backgroundSize: 'cover',
          backgroundPosition: 'center top',
          backgroundRepeat: 'no-repeat',
          backgroundColor: '#DCEAF9',
        }}
      />
      {/* Desktop background: generated widescreen scene */}
      <div
        className="fixed inset-0 -z-10 bg-cover bg-center bg-no-repeat hidden md:block"
        style={{ backgroundImage: "url('/login-bg-desktop.webp')" }}
      />

      {/* Logo + tagline over the illustration */}
      <div className="flex flex-col items-center px-6 pt-[calc(env(safe-area-inset-top)+0.5rem)] md:pt-0 md:mb-7">
        <div className="w-28 h-28 md:w-32 md:h-32">
          <img
            src="/logo-new.png"
            alt="GuestCheckout"
            className="w-full h-full object-contain"
          />
        </div>
        <p className="mt-3 text-[22px] md:text-2xl font-extrabold text-slate-900 tracking-tight">
          Smart Checkout Management
        </p>
      </div>

      {/* Spacer pushes the sheet to the bottom, revealing the village (mobile) */}
      <div className="flex-1 md:hidden" />

      {/* Login Card — compact, static (no animation), narrower to reveal more background */}
      <div className="w-full flex justify-center px-4 pb-[calc(env(safe-area-inset-bottom)+1.25rem)] md:px-4 md:pb-3">
        <div
          className="w-full max-w-[330px] md:max-w-md bg-white/80 md:bg-white backdrop-blur-lg md:backdrop-blur-none rounded-[1.75rem] md:rounded-[2rem] shadow-[0_12px_44px_rgba(15,23,42,0.20)] md:shadow-2xl px-5 md:px-8 pt-4 md:pt-7 pb-4 md:pb-7"
        >
          <form onSubmit={handleLogin} className="space-y-3" data-testid="login-form">
            <div>
              <label className="block text-sm font-bold text-slate-800 mb-2">Email Address</label>
              <div className="relative">
                <Mail size={17} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" />
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  placeholder="you@company.com"
                  data-testid="email-input"
                  className="w-full h-12 pl-9 pr-3 rounded-2xl bg-white border border-slate-200 text-base text-slate-800 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-emerald-400 focus:border-transparent transition-shadow"
                />
              </div>
            </div>

            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="text-sm font-bold text-slate-800">Password</label>
                <button
                  type="button"
                  onClick={() => navigate('/forgot-password')}
                  className="text-sm text-blue-600 hover:text-blue-700 font-semibold"
                  data-testid="forgot-password-link"
                >
                  Forgot password?
                </button>
              </div>
              <div className="relative">
                <Lock size={17} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" />
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  placeholder="••••••••"
                  data-testid="password-input"
                  className="w-full h-12 pl-9 pr-10 rounded-2xl bg-white border border-slate-200 text-base text-slate-800 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-emerald-400 focus:border-transparent transition-shadow"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 inset-y-0 flex items-center text-slate-400 hover:text-slate-600"
                  data-testid="toggle-password-visibility"
                >
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full h-12 rounded-2xl font-bold text-base text-white flex items-center justify-center gap-2 bg-gradient-to-r from-[#57B22E] to-[#3E9A1C] shadow-lg shadow-green-600/30 hover:from-[#4CA327] hover:to-[#358916] active:scale-[0.99] transition-all disabled:opacity-60"
              data-testid="login-button"
            >
              {loading ? (
                <div className="spinner-3d w-6 h-6 border-2"></div>
              ) : (
                <>
                  <LogIn size={20} />
                  Sign In
                  <ArrowRight size={20} />
                </>
              )}
            </button>
          </form>

          {/* Google Sign-In */}
          <div className="mt-3">
            <GoogleSignInButton label="Sign in with Google" testId="login-google-button" />
          </div>

          {/* Apple Sign-In (iOS only) */}
          <div className="mt-3">
            <AppleSignInButton label="Sign in with Apple" testId="login-apple-button" />
          </div>

          {/* Divider (no white box; thin lines flank the text) */}
          <div className="my-3 flex items-center gap-3">
            <div className="flex-1 border-t border-slate-200"></div>
            <span className="text-sm text-slate-500 font-medium whitespace-nowrap">New to GuestCheckout?</span>
            <div className="flex-1 border-t border-slate-200"></div>
          </div>

          {/* Register Link */}
          <button
            onClick={() => navigate('/register')}
            className="w-full h-12 rounded-2xl font-bold text-blue-600 border-2 border-blue-500/70 bg-white hover:bg-blue-50 active:scale-[0.99] flex items-center justify-center gap-2 transition-all"
            data-testid="register-link"
          >
            Create Account
            <ArrowRight size={18} />
          </button>

          {/* Footer (desktop only; mobile matches the clean mockup) */}
          <div className="mt-5 text-center hidden md:block">
            <p className="text-[11px] text-slate-400 mb-1.5">
              © {new Date().getFullYear()} GuestCheckoutapp. All rights reserved.
            </p>
            <div className="flex items-center justify-center gap-3 text-[11px] text-slate-400">
              <button onClick={() => navigate('/privacy')} className="hover:text-blue-600 transition-colors">Privacy</button>
              <span>•</span>
              <button onClick={() => navigate('/terms')} className="hover:text-blue-600 transition-colors">Terms</button>
              <span>•</span>
              <button onClick={() => navigate('/contact')} className="hover:text-blue-600 transition-colors" data-testid="login-contact-link">Contact</button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
